import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Form, Table, Alert, Modal, Badge, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({
    client_id: '',
    client_secret: '',
    name: '',
    is_active: true
  });
  const [generatedToken, setGeneratedToken] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch OAuth2 clients
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/oauth2/clients', {
        headers: headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to view OAuth2 clients.');
        } else {
          throw new Error(`Failed to fetch clients: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      setClients(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Create new OAuth2 client
  const createClient = async () => {
    try {
      setCreating(true);
      setError(null);
      
      if (!newClient.name.trim()) {
        throw new Error('Client name is required');
      }

      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/oauth2/clients', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(newClient)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to create OAuth2 clients.');
        } else {
          throw new Error(errorData.detail || `Failed to create client: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      setClients([...clients, data]);
      setShowCreateModal(false);
      setNewClient({ client_id: '', client_secret: '', name: '', is_active: true });
    } catch (err) {
      setError(err.message);
      console.error('Error creating client:', err);
    } finally {
      setCreating(false);
    }
  };

  // Delete OAuth2 client
  const deleteClient = async () => {
    try {
      setDeleting(true);
      setError(null);
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api/oauth2/clients/${selectedClient.client_id}`, {
        method: 'DELETE',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to delete OAuth2 clients.');
        } else if (response.status === 404) {
          throw new Error('Client not found. It may have been deleted already.');
        } else {
          throw new Error(errorData.detail || `Failed to delete client: ${response.status} ${response.statusText}`);
        }
      }

      setClients(clients.filter(client => client.client_id !== selectedClient.client_id));
      setShowDeleteModal(false);
      setSelectedClient(null);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting client:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Generate token for client
  const generateToken = async (clientId, clientSecret) => {
    try {
      setError(null);
      const response = await fetch('/api/oauth2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'client_credentials',
          client_id: clientId,
          client_secret: clientSecret,
          scope: 'webhook'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to generate token: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedToken(data);
    } catch (err) {
      setError(err.message);
      console.error('Error generating token:', err);
    }
  };

  // Generate random client ID and secret
  const generateRandomCredentials = () => {
    const clientId = `client_${Math.random().toString(36).substr(2, 9)}`;
    const clientSecret = `secret_${Math.random().toString(36).substr(2, 12)}`;
    setNewClient({
      ...newClient,
      client_id: clientId,
      client_secret: clientSecret
    });
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading OAuth2 clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>🔧 Settings</h2>
            <Button 
              variant="primary" 
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
            >
              ➕ Create New OAuth2 Client
            </Button>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {generatedToken && (
            <Alert variant="success" onClose={() => setGeneratedToken(null)} dismissible>
              <h5>🎫 Generated Token</h5>
              <p><strong>Access Token:</strong></p>
              <div className="position-relative">
                <code className="d-block p-3 bg-light rounded mb-2" style={{ wordBreak: 'break-all', fontSize: '0.9em' }}>
                  {generatedToken.access_token}
                </code>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={() => copyToClipboard(generatedToken.access_token)}
                  title="Copy to clipboard"
                >
                  📋
                </Button>
              </div>
              <div className="row">
                <div className="col-md-4">
                  <strong>Token Type:</strong> {generatedToken.token_type}
                </div>
                <div className="col-md-4">
                  <strong>Expires In:</strong> {generatedToken.expires_in} seconds
                </div>
                <div className="col-md-4">
                  <strong>Scope:</strong> {generatedToken.scope || 'N/A'}
                </div>
              </div>
            </Alert>
          )}

          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">🔑 OAuth2 Clients</h5>
            </Card.Header>
            <Card.Body>
              {clients.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <span style={{ fontSize: '3rem' }}>🔑</span>
                  </div>
                  <h5>No OAuth2 clients found</h5>
                  <p className="text-muted">Create your first client to get started with OAuth2 authentication.</p>
                  <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create Your First Client
                  </Button>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead className="table-dark">
                    <tr>
                      <th>Client ID</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <tr key={client.client_id}>
                        <td>
                          <code className="bg-light px-2 py-1 rounded">{client.client_id}</code>
                        </td>
                        <td>
                          <strong>{client.name}</strong>
                        </td>
                        <td>
                          <Badge bg={client.is_active ? 'success' : 'secondary'}>
                            {client.is_active ? '✅ Active' : '❌ Inactive'}
                          </Badge>
                        </td>
                        <td>
                          {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => generateToken(client.client_id, client.client_secret)}
                              title="Generate Token"
                            >
                              🎫 Token
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => copyToClipboard(client.client_id)}
                              title="Copy Client ID"
                            >
                              📋 ID
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => copyToClipboard(client.client_secret)}
                              title="Copy Client Secret"
                            >
                              🔐 Secret
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setSelectedClient(client);
                                setShowDeleteModal(true);
                              }}
                              title="Delete Client"
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* OAuth2 Configuration Guide */}
          <Card className="mt-4 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">📖 OAuth2 Configuration Guide</h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6">
                  <h6>🔧 For VAPI Integration:</h6>
                  <ol>
                    <li>Create a new OAuth2 client using the button above</li>
                    <li>Use the generated Client ID and Client Secret in your VAPI configuration</li>
                    <li>Set the OAuth2 URL to: <code>https://lms.iconinc.co.uk/api/oauth2/token</code></li>
                    <li>Optional: Set scope to <code>webhook</code></li>
                  </ol>
                  
                  <h6>🌐 Webhook Endpoint:</h6>
                  <p><code>https://lms.iconinc.co.uk/api/vapi/webhook</code></p>
                </div>
                
                <div className="col-md-6">
                  <h6>📡 Supported Events:</h6>
                  <ul>
                    <li><code>call.ended</code> - Creates user enquiries automatically</li>
                  </ul>
                  
                  <h6>🤖 Target Assistants:</h6>
                  <ul>
                    <li><code>iconincBookingAgent</code></li>
                    <li><code>iconincViewingAgent</code></li>
                  </ul>
                  
                  <h6>⚡ Quick Actions:</h6>
                  <ul>
                    <li>Click <strong>🎫 Token</strong> to generate an access token</li>
                    <li>Click <strong>📋 ID</strong> to copy Client ID</li>
                    <li>Click <strong>🔐 Secret</strong> to copy Client Secret</li>
                  </ul>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Create Client Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New OAuth2 Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Client Name *</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter client name (e.g., VAPI Webhook Client)"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                A descriptive name for this OAuth2 client
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Client ID</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Enter client ID or leave empty to auto-generate"
                  value={newClient.client_id}
                  onChange={(e) => setNewClient({ ...newClient, client_id: e.target.value })}
                />
                <Button variant="outline-secondary" onClick={generateRandomCredentials}>
                  🎲 Generate
                </Button>
              </div>
              <Form.Text className="text-muted">
                Leave empty to auto-generate a secure client ID
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Client Secret</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Enter client secret or leave empty to auto-generate"
                  value={newClient.client_secret}
                  onChange={(e) => setNewClient({ ...newClient, client_secret: e.target.value })}
                />
                <Button variant="outline-secondary" onClick={generateRandomCredentials}>
                  🎲 Generate
                </Button>
              </div>
              <Form.Text className="text-muted">
                Leave empty to auto-generate a secure client secret
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={newClient.is_active}
                onChange={(e) => setNewClient({ ...newClient, is_active: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Inactive clients cannot be used for authentication
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={createClient}
            disabled={creating || !newClient.name.trim()}
          >
            {creating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              'Create Client'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Client Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete OAuth2 Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <span style={{ fontSize: '3rem' }}>⚠️</span>
          </div>
          <p>Are you sure you want to delete the client <strong>{selectedClient?.name}</strong>?</p>
          <p className="text-danger"><strong>This action cannot be undone.</strong></p>
          <p className="text-muted">
            Any applications using this client will no longer be able to authenticate.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={deleteClient}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Client'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Settings; 